"use client";

import i18n from "@/lang/i18n";
import { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  ButtonBase,
  CircularProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDarkColors } from "@/hooks/useDarkColors";
import { useAuthStore } from "@/stores/authStore";
import { useTranslator } from "@/lang";
import {
  TEMP_TITLE_EXTRABOLD_XL,
  TEMP_TEXT_REGULAR_M,
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_BOLD_XS,
  TEMP_TEXT_REGULAR_XS,
  TEMP_TEXT_BOLD_L,
} from "@/constants/tempTypo";
import {
  deleteUserFeed,
  getMyUserFeedList,
  getUserFeedDetail,
  type UserFeedItem,
  type UserFeedCommentItem,
  type UserFeedAuthor,
} from "@/api/userFeedApi";
import { Menu, MenuItem } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { usePortalStore } from "@/stores/portalStore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { getAssetUrl } from "@/utils/assetUrl";
import { APP_MAX_WIDTH } from "@/components/PageContent/PageContent";
import { renderFeedBody } from "../_components/renderFeedBody";
import { stripFeedActions } from "../_components/stripFeedActions";
import ReactionListSheet, {
  REACTION_EMOJI,
} from "../_components/ReactionListSheet";
import { NotificationBell } from "@/components/NotificationBell";
import { Icon } from "@/components/icons";
import { formatCount } from "@/utils/formatCount";
import FeedComposerSheet from "../_components/FeedComposerSheet";
import FeedImage from "../_components/FeedImage";

// ============================================================
// MyFeedListContent
// - 페이지(/my-feed/page.tsx)와 NavigationStack 라우트(MyFeedRoute) 둘 다에서 사용
// - onBack: 뒤로가기 동작 주입. 바텀 네비 탭 진입 시엔 주입하지 않아 버튼이 숨겨진다.
// - onOpenDetail: 디테일 진입 동작 주입 (스택일 땐 stack.push, 페이지일 땐 router.push)
// ============================================================

interface Props {
  /** 뒤로가기 동작. 바텀 네비 탭으로 진입한 경우엔 미주입 → 뒤로가기 버튼 숨김. */
  onBack?: () => void;
  onOpenDetail: (feedId: string) => void;
  /** 스택 내부면 true — 외곽 wrapper(maxWidth/사이드 마진) 생략 */
  inStack?: boolean;
  /** 홈('/') 안에 제자리로 끼워 넣을 때 true — 홈 스크롤 컨테이너(feed-scroll-container)를 그대로 쓰고
   *  외곽 wrapper 없이 렌더. 헤더는 sticky(top 0)로 홈 헤더 자리를 대신한다. */
  embedded?: boolean;
}

export default function MyFeedListContent({
  onBack,
  onOpenDetail,
  inStack = false,
  embedded = false,
}: Props) {
  const c = useDarkColors();
  const qc = useQueryClient();
  const { t, f } = useTranslator();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const isAuthInitialized = useAuthStore((s) => s.isAuthInitialized);
  const [composerOpen, setComposerOpen] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const openModal = usePortalStore((s) => s.openModal);
  const closeById = usePortalStore((s) => s.closeById);

  const openMyFeedInfoModal = () => {
    let id = "";
    id = openModal({
      content: <MyFeedInfoModalContent onClose={() => closeById(id)} />,
    });
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["user-feed-my-list"],
    queryFn: ({ pageParam }) =>
      getMyUserFeedList({
        limit: 20,
        ...(pageParam ? { cursor: pageParam as number } : {}),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isLoggedIn,
    // 5초마다 자동 새로고침 — 워커가 비동기로 댓글/반응을 채워주므로 실시간 느낌 부여.
    // 백그라운드 탭에서는 폴링 중지(refetchIntervalInBackground 기본값 false).
    refetchInterval: isLoggedIn ? 5_000 : false,
  });

  const handleManualRefresh = useCallback(async () => {
    if (isManualRefreshing) return;
    setIsManualRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsManualRefreshing(false);
    }
  }, [isManualRefreshing, refetch]);

  const items = (data?.pages.flatMap((p) => p.items) ?? []) as UserFeedItem[];
  // 실제 값이 도착하기 전엔 undefined로 두고, 텍스트는 값이 확정된 뒤에만 노출.
  // (이전 코드: `?? 3`이 디폴트로 깔려서 데이터 도착 시 "3 남음" → "0 남음"으로 깜박이는 문제)
  const freeRemaining: number | undefined = data?.pages[0]?.freePostsRemaining;
  const freeRemainingForSheet = freeRemaining ?? 0;

  const onComposed = useCallback(() => {
    setComposerOpen(false);
    qc.invalidateQueries({ queryKey: ["user-feed-my-list"] });
    qc.invalidateQueries({ queryKey: ["notification-unread"] });
  }, [qc]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading],
  );

  const inner = (
    <>
      {/* 헤더 — 상위 PageContent/StackScreen이 이미 safe-area-top을 적용하므로 여기선 추가하지 않음 */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: c.bg,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 64,
            px: "20px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onBack && (
              <ButtonBase onClick={onBack} aria-label={t("my_feed.aria.back")}>
                <ArrowBackIosNewIcon sx={{ fontSize: 18, color: c.fg }} />
              </ButtonBase>
            )}
            <Typography
              onClick={openMyFeedInfoModal}
              sx={{
                ...TEMP_TITLE_EXTRABOLD_XL,
                color: c.fg,
                ml: onBack ? 1 : 0,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                "&:active": { opacity: 0.7 },
              }}
            >
              {t("my_feed.title")}
            </Typography>
            <ButtonBase
              onClick={openMyFeedInfoModal}
              aria-label={t("my_feed.aria.info")}
              sx={{
                ml: "4px",
                width: 22,
                height: 22,
                borderRadius: "9999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: c.fgLow,
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </ButtonBase>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <ButtonBase
              onClick={handleManualRefresh}
              disabled={isManualRefreshing}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "999px",
                color: c.fg,
                opacity: isManualRefreshing ? 0.5 : 1,
              }}
              aria-label={t("my_feed.aria.refresh")}
            >
              <RefreshIcon
                sx={{
                  fontSize: 22,
                  // 누르는 즉시 회전 피드백 — 자연스럽게 새로고침 표현
                  animation: isManualRefreshing
                    ? "myfeed-spin 0.8s linear infinite"
                    : "none",
                  "@keyframes myfeed-spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </ButtonBase>

            <NotificationBell />

            <ButtonBase
              onClick={() => setComposerOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "999px",
                bgcolor: c.primary,
                color: "#fff",
              }}
              aria-label={t("my_feed.aria.compose")}
            >
              <AddIcon sx={{ fontSize: 22 }} />
            </ButtonBase>
          </Box>
        </Box>

        {/* 무료 회수 안내 — 데이터가 실제 도착하고, 남은 회수가 1 이상일 때만 노출.
            기존: `?? 3` 디폴트로 항상 잠깐 떴다가 사라지는 깜박임 발생. */}
        {freeRemaining !== undefined && freeRemaining > 0 && (
          <Box sx={{ px: "20px", pb: "12px" }}>
            <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLow }}>
              {f(t("my_feed.free_remaining"), [freeRemaining])}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 리스트 — auth 초기화 / 첫 페이지 fetch 완료 전까지 항상 스켈레톤.
          (예전: isLoading/!data 분기에서 isLoggedIn이 미정일 때 EmptyState로 빠져 깜빡임 발생) */}
      {!isAuthInitialized || (isLoggedIn && (isLoading || !data)) ? (
        <FeedListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState onCompose={() => setComposerOpen(true)} />
      ) : (
        <Box>
          {items.map((it, idx) => {
            const isLast = idx === items.length - 1;
            return (
              <FeedListItem
                key={it.id}
                it={it}
                onOpenDetail={onOpenDetail}
                rowRef={isLast ? lastItemRef : undefined}
              />
            );
          })}

          {isFetchingNextPage && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
      )}

      <FeedComposerSheet
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onComposed={onComposed}
        freePostsRemaining={freeRemainingForSheet}
      />
    </>
  );

  // 홈 안 제자리 전환: 홈 Container 가 이미 폭을 잡으므로 wrapper 없이, 짧아도 화면을 채운다
  if (embedded) {
    return <Box sx={{ minHeight: "100vh", bgcolor: c.bg }}>{inner}</Box>;
  }

  // 스택 내부: 외곽 wrapper 없이 그대로 (StackScreen이 이미 컨테이너 제공)
  if (inStack) {
    return <Box sx={{ minHeight: "100%", bgcolor: c.bg }}>{inner}</Box>;
  }

  // 직접 URL 접근: 상위 PageContent가 이미 100dvh 스크롤 컨테이너 + safe-area 처리하므로,
  // 여기선 별도 minHeight 100vh를 강제하지 않음 (그러지 않으면 컨텐츠가 1개여도 스크롤 발생)
  return (
    <Box sx={{ bgcolor: c.bg, maxWidth: APP_MAX_WIDTH, mx: "auto" }}>
      {inner}
    </Box>
  );
}

// 외부 리스트에서 캐릭터들의 이모지 반응 칩.
// - 규격 통일: 이모지 1개/2개든 chip의 시각 높이/패딩 동일.
// - onClick 제공 시 ButtonBase로 렌더 → 클릭하면 디테일의 ReactionListSheet와 동일한 UI로 누가 어떤 반응 남겼는지 노출.
function ReactionSummary({
  reactions,
  onClick,
}: {
  reactions?: Partial<Record<string, number>>;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const c = useDarkColors();
  const { t } = useTranslator();
  if (!reactions) return null;
  const entries = Object.entries(reactions)
    .filter(([, n]) => typeof n === "number" && n > 0)
    .sort((a, b) => (b[1] as number) - (a[1] as number))
    .slice(0, 2);
  if (entries.length === 0) return null;
  const total = entries.reduce((acc, [, n]) => acc + (n as number), 0);
  const chipSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: 30,
    px: "10px",
    borderRadius: "999px",
    bgcolor: c.bgInset,
    WebkitTapHighlightColor: "transparent",
  } as const;
  const content = (
    <>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
        {entries.map(([type]) => (
          <span
            key={type}
            style={{
              fontSize: 14,
              lineHeight: 1,
              display: "inline-block",
              minWidth: 14,
              textAlign: "center",
            }}
          >
            {REACTION_EMOJI[type as keyof typeof REACTION_EMOJI] ?? "❤️"}
          </span>
        ))}
      </Box>
      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
        {total}
      </Typography>
    </>
  );
  if (onClick) {
    return (
      <ButtonBase onClick={onClick} sx={chipSx} aria-label={t("my_feed.aria.reactions")}>
        {content}
      </ButtonBase>
    );
  }
  return <Box sx={chipSx}>{content}</Box>;
}

function FeedListSkeleton() {
  const c = useDarkColors();
  // 빈 EmptyState → 데이터 흐름과 깜빡거리는 걸 막기 위한 정적 스켈레톤(3개 행).
  // 화려한 shimmer는 생략 — 단순 placeholder가 가장 거슬리지 않음.
  return (
    <Box>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            px: "20px",
            py: "16px",
            borderBottom: `1px solid ${c.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: c.bgInset,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 120,
                  height: 12,
                  borderRadius: "4px",
                  bgcolor: c.bgInset,
                  mb: "6px",
                }}
              />
              <Box
                sx={{
                  width: 64,
                  height: 10,
                  borderRadius: "4px",
                  bgcolor: c.bgInset,
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 12,
              borderRadius: "4px",
              bgcolor: c.bgInset,
              mb: "8px",
            }}
          />
          <Box
            sx={{
              width: "72%",
              height: 12,
              borderRadius: "4px",
              bgcolor: c.bgInset,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function EmptyState({ onCompose }: { onCompose: () => void }) {
  const c = useDarkColors();
  const { t } = useTranslator();
  return (
    <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
      <Typography
        sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg, mb: 1, whiteSpace: "pre-line" }}
      >
        {t("my_feed.empty.title")}
      </Typography>
      <Typography
        sx={{
          ...TEMP_TEXT_REGULAR_M,
          color: c.fgLow,
          mb: 3,
          whiteSpace: "pre-line",
          lineHeight: 1.6,
        }}
      >
        {t("my_feed.empty.privacy")}
      </Typography>
      <ButtonBase
        onClick={onCompose}
        sx={{
          bgcolor: c.primary,
          color: "#fff",
          px: 3,
          py: 1.25,
          borderRadius: 999,
          ...TEMP_TEXT_BOLD_M,
        }}
      >
        {t("my_feed.empty.cta")}
      </ButtonBase>
    </Box>
  );
}

function MyFeedInfoModalContent({ onClose }: { onClose: () => void }) {
  const c = useDarkColors();
  const { t } = useTranslator();
  return (
    <Box
      sx={{
        bgcolor: c.bg,
        p: "28px 24px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: c.primaryLow,
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 30, color: c.primary }} />
        </Box>
        <Typography
          sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg, textAlign: "center", whiteSpace: "pre-line", mt: "4px" }}
        >
          {t("my_feed.empty.title").split("\n").slice(1).join("\n")}
        </Typography>
      </Box>

      <Box sx={{ bgcolor: c.bgInset, borderRadius: "12px", p: "14px 16px" }}>
        <Typography
          sx={{
            ...TEMP_TEXT_REGULAR_M,
            color: c.fgLow,
            whiteSpace: "pre-line",
            lineHeight: 1.6,
            textAlign: "center",
          }}
        >
          {t("my_feed.empty.privacy")}
        </Typography>
      </Box>

      <Box
        onClick={onClose}
        sx={{
          bgcolor: c.primary,
          borderRadius: "16px",
          py: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          mt: "2px",
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: "#fff" }}>{t("common.confirm")}</Typography>
      </Box>
    </Box>
  );
}

function FeedListItem({
  it,
  onOpenDetail,
  rowRef,
}: {
  it: UserFeedItem;
  onOpenDetail: (feedId: string) => void;
  rowRef?: (node: HTMLDivElement | null) => void;
}) {
  const c = useDarkColors();
  const qc = useQueryClient();
  const { t, language } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openBottomSheet = usePortalStore((s) => s.openBottomSheet);

  const { data: detail, isFetching } = useQuery({
    queryKey: ["user-feed-detail", it.id],
    queryFn: () => getUserFeedDetail(it.id),
    enabled: expanded,
    staleTime: 15_000,
  });

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const openConfirm = usePortalStore((s) => s.openConfirm);
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuAnchor(null);
    openConfirm({
      title: t("my_feed.list.delete_confirm_title"),
      titleIcon: (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "#FFE5E5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 26, color: "#e53935" }} />
        </Box>
      ),
      subtitle: t("my_feed.list.delete_confirm_subtitle"),
      cancelText: t("common.cancel"),
      confirmText: t("my_feed.common.delete"),
      onConfirm: async () => {
        try {
          await deleteUserFeed(it.id);
          qc.invalidateQueries({ queryKey: ["user-feed-my-list"] });
        } catch {
          // fail silently
        }
      },
    });
  };

  return (
    <Box
      ref={rowRef}
      onClick={() => onOpenDetail(it.id)}
      sx={{
        px: "20px",
        py: "16px",
        borderBottom: `1px solid ${c.border}`,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        {/* 프로필 이미지: 있으면 그 이미지를, 없으면 이니셜 placeholder */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: c.bgInset,
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...(it.authorProfileImageUrl
              ? {
                  backgroundImage: `url(${getAssetUrl(it.authorProfileImageUrl, "large")})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : null),
          }}
        >
          {!it.authorProfileImageUrl && (
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fgLow }}>
              {(it.authorUserName ?? t("my_feed.author.me")).slice(0, 1)}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>
            {it.authorUserName ?? t("my_feed.author.me")}
          </Typography>
          <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLower }}>
            {formatRelative(it.createdAt, t, language)}
          </Typography>
        </Box>

        {/* 점3개 메뉴 */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{ color: c.fgLow }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClick={stop}
          onClose={(e: any) => {
            e?.stopPropagation?.();
            setMenuAnchor(null);
          }}
        >
          <MenuItem onClick={handleDelete} sx={{ color: "#e53935" }}>
            {t("my_feed.list.delete_menu")}
          </MenuItem>
        </Menu>
      </Box>

      <Typography
        component="div"
        sx={{
          ...TEMP_TEXT_REGULAR_M,
          color: c.fg,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {renderFeedBody(
          it.content,
          (it.mentionedCharacters ?? []).map((m) => ({
            characterId: m.characterId,
            name: pickName(m.name),
            nameVariants: collectNameVariants(m.name),
            profileImagePath: m.profileImagePath,
          })),
          c.primary,
        )}
      </Typography>

      {it.imagePath && (
        <FeedImage path={it.imagePath} variant="medium" maxHeight={360} />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          mt: 1,
          ml: "-10px",
        }}
      >
        {/* 이모지 반응 — 좌측. 반응이 1개 이상 있을 때만 고정 폭(90px) 슬롯으로 렌더.
            없으면 슬롯 자체를 안 박아서 댓글이 자연스럽게 왼쪽으로 붙는다.
            칩 클릭 → 누가 어떤 반응 남겼는지 보여주는 ReactionListSheet 오픈 (디테일과 동일 UI). */}
        {hasReactions(it.feedReactions) && (
          <Box
            sx={{
              width: 90,
              height: 30,
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <ReactionSummary
              reactions={it.feedReactions}
              onClick={(e) => {
                stop(e);
                openBottomSheet({
                  content: (
                    <ReactionListSheet
                      feedId={it.id}
                      targetType="FEED"
                      targetId={it.id}
                    />
                  ),
                  midHeightPercent: 50,
                  maxHeightPercent: 90,
                });
              }}
            />
          </Box>
        )}

        {/* 댓글 — 반응 슬롯이 있으면 그 오른쪽, 없으면 가장 왼쪽. */}
        <ButtonBase
          onClick={(e) => {
            stop(e);
            setExpanded((v) => !v);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            px: "10px",
            py: "8px",
            borderRadius: "999px",
            WebkitTapHighlightColor: "transparent",
          }}
          aria-label={t("my_feed.aria.comments")}
          aria-expanded={expanded}
        >
          <Icon name="icCommentOutlined" size={22} color={c.fgLow} />
          <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
            {formatCount(it.commentCount)}
          </Typography>
        </ButtonBase>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          onClick={() => onOpenDetail(it.id)}
          sx={{
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${c.border}`,
            cursor: "pointer",
          }}
        >
          {isFetching && !detail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={16} />
            </Box>
          ) : !detail || detail.comments.length === 0 ? (
            <Typography
              sx={{
                ...TEMP_TEXT_REGULAR_XS,
                color: c.fgLower,
                py: 2,
                textAlign: "center",
              }}
            >
              {t("my_feed.list.muses_will_react")}
            </Typography>
          ) : (
            <Box>
              {detail.comments.map((cm) => (
                <InlineComment key={cm.id} comment={cm} />
              ))}
              <ButtonBase
                onClick={(e) => {
                  stop(e);
                  onOpenDetail(it.id);
                }}
                sx={{
                  ...TEMP_TEXT_BOLD_XS,
                  color: c.primary,
                  py: 1,
                  mt: 0.5,
                }}
              >
                {t("my_feed.list.view_all")}
              </ButtonBase>
            </Box>
          )}
        </Box>
      </Collapse>

    </Box>
  );
}

function InlineComment({ comment }: { comment: UserFeedCommentItem }) {
  const c = useDarkColors();
  const { t } = useTranslator();
  const avatarUrl =
    comment.author.authorType === "CHARACTER" &&
    comment.author.characterProfileImagePath
      ? getAssetUrl(comment.author.characterProfileImagePath, "large")
      : comment.author.userProfileImageUrl
        ? getAssetUrl(comment.author.userProfileImageUrl, "large")
        : "";
  const name = inlineAuthorDisplay(comment.author, t);
  return (
    <Box sx={{ display: "flex", gap: 1, py: 0.75 }}>
      {avatarUrl && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: c.bgInset,
            overflow: "hidden",
            flexShrink: 0,
            backgroundImage: `url(${avatarUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fg }}>
          {name}
        </Typography>
        <Typography
          sx={{
            ...TEMP_TEXT_REGULAR_XS,
            color: c.fg,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {comment.author.authorType === "CHARACTER"
            ? stripFeedActions(comment.content)
            : comment.content}
        </Typography>
      </Box>
    </Box>
  );
}

function inlineAuthorDisplay(
  author: UserFeedAuthor,
  t: (key: string) => string,
): string {
  if (author.authorType === "USER") return author.userName ?? t("my_feed.author.me");
  return pickName(author.characterName) || t("my_feed.author.muse");
}

function pickName(name: any): string {
  if (!name) return "";
  if (typeof name === "string") return name;
  if (typeof name === "object")
    return name[i18n.language] ?? name.ko ?? name.en ?? Object.values(name)[0] ?? "";
  return "";
}

// 반응이 1개라도 있는지 — 좌측 90px 슬롯을 렌더할지 판단.
// 빈 객체 / undefined / 모든 카운트 0 모두 false.
function hasReactions(
  reactions: Partial<Record<string, number>> | undefined,
): boolean {
  if (!reactions) return false;
  return Object.values(reactions).some(
    (n) => typeof n === "number" && n > 0,
  );
}

// 다국어 이름 모두 매칭 후보로 추출 — 본문에 @영문이름/@한글이름 어느 쪽으로 적혀도 캐릭 매칭되게.
function collectNameVariants(name: any): string[] {
  if (!name) return [];
  if (typeof name === "string") return [name];
  if (typeof name === "object")
    return Object.values(name).filter(
      (v): v is string => typeof v === "string" && v.length > 0,
    );
  return [];
}

// 다국어 상대시간 포매팅 — t()/f()로 단위 키 조회, 7일 초과 시 현재 언어 로케일 기준 날짜.
function formatRelative(
  iso: string,
  t: (key: string) => string,
  language: string,
): string {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  const fmt = (template: string, n: number) =>
    template.replace(/\{0\}/g, String(n));
  if (diff < 60) return fmt(t("my_feed.time.seconds_ago"), diff);
  if (diff < 3600) return fmt(t("my_feed.time.minutes_ago"), Math.floor(diff / 60));
  if (diff < 86400) return fmt(t("my_feed.time.hours_ago"), Math.floor(diff / 3600));
  if (diff < 86400 * 7)
    return fmt(t("my_feed.time.days_ago"), Math.floor(diff / 86400));
  // 7일 이상: 현재 언어 로케일 기준 (ko/en/ja/zh/zh-TW 모두 정상 매핑)
  const locale = language === "zh" ? "zh-CN" : language === "zh_TW" ? "zh-TW" : language;
  return d.toLocaleDateString(locale);
}
